import { useState } from 'react';
import { useGetAttemptsQuery } from '../store/adminApi';
import { Card } from '../components/Card';
import { Table, Column } from '../components/Table';
import { Pagination } from '../components/Pagination';
import { FilterInput } from '../components/FilterInput';
import { Modal } from '../components/Modal';
import { ErrorState } from '../components/LoadingSpinner';
import { StatusBadge, getDeliveryStatusType } from '../components/StatusBadge';
import type { AttemptListItem, AttemptQueryParams } from '../types/api';
import { RelativeTime } from '../components/admin/TableCells';
import { PageHeader, TableSkeleton } from '../components/admin/AdminUI';
import { MessageOutbox } from './MessageOutbox';

const LIMIT = 50;

export function AttemptsPage() {
  const [filters, setFilters] = useState<AttemptQueryParams>({
    limit: LIMIT,
    offset: 0,
  });
  const [selectedAttempt, setSelectedAttempt] = useState<AttemptListItem | null>(
    null
  );

  const [channel, setChannel] = useState<'push' | 'email' | 'sms'>('push');
  const { data, isLoading, error } = useGetAttemptsQuery(filters, { skip: channel !== 'push' });

  const updateFilter = (key: keyof AttemptQueryParams, value: string) => {
    setFilters((prev: AttemptQueryParams) => ({
      ...prev,
      [key]: value || undefined,
      offset: 0,
    }));
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  const columns: Column<AttemptListItem>[] = [
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge
          status={getDeliveryStatusType(row.status)}
          label={row.status.toUpperCase()}
        />
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (row) => (
        <StatusBadge
          status={row.provider === 'apns' ? 'info' : 'success'}
          label={row.provider.toUpperCase()}
        />
      ),
    },
    { key: 'appId', header: 'App ID' },
    { key: 'eventType', header: 'Event Type' },
    {
      key: 'notificationId',
      header: 'Notification ID',
      className: 'font-mono text-xs truncate max-w-[150px]',
    },
    { key: 'workspaceId', header: 'Workspace', className: 'font-mono text-xs' },
    { key: 'userId', header: 'User', className: 'font-mono text-xs' },
    {
      key: 'errorCode',
      header: 'Error',
      render: (row) =>
        row.errorCode ? (
          <span className="text-red-600 font-mono text-xs">{row.errorCode}</span>
        ) : (
          '-'
        ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (row) => <RelativeTime iso={row.createdAt} never="—" />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Delivery Attempts"
        subtitle={<>Push, email and SMS deliveries, with retry and run-now for the email and SMS queues</>}
      />

      <div role="tablist" aria-label="Channel" className="inline-flex rounded-lg bg-slate-100 p-1">
        {(['push', 'email', 'sms'] as const).map((c) => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={channel === c}
            onClick={() => setChannel(c)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium ${
              channel === c ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {c === 'push' ? 'Push' : c === 'email' ? 'Email' : 'SMS'}
          </button>
        ))}
      </div>

      {channel !== 'push' ? (
        <MessageOutbox key={channel} channel={channel} />
      ) : (
      <>

      {/* Filters */}
      <Card title="Filters">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <FilterInput
            label="App ID"
            value={filters.appId || ''}
            onChange={(v) => updateFilter('appId', v)}
            placeholder="e.g., levelup"
          />
          <FilterInput
            label="Workspace ID"
            value={filters.workspaceId || ''}
            onChange={(v) => updateFilter('workspaceId', v)}
            placeholder="UUID"
          />
          <FilterInput
            label="User ID"
            value={filters.userId || ''}
            onChange={(v) => updateFilter('userId', v)}
            placeholder="UUID"
          />
          <FilterInput
            label="Status"
            value={filters.status || ''}
            onChange={(v) => updateFilter('status', v)}
            type="select"
            options={[
              { value: 'queued', label: 'Queued' },
              { value: 'sent', label: 'Sent' },
              { value: 'failed', label: 'Failed' },
              { value: 'skipped', label: 'Skipped' },
            ]}
          />
          <FilterInput
            label="Provider"
            value={filters.provider || ''}
            onChange={(v) => updateFilter('provider', v)}
            type="select"
            options={[
              { value: 'apns', label: 'APNs (iOS)' },
              { value: 'fcm', label: 'FCM (Android)' },
            ]}
          />
          <FilterInput
            label="Notification ID"
            value={filters.notificationId || ''}
            onChange={(v) => updateFilter('notificationId', v)}
            placeholder="Notification ID"
          />
          <FilterInput
            label="From"
            value={filters.from || ''}
            onChange={(v) => updateFilter('from', v)}
            type="datetime-local"
          />
          <FilterInput
            label="To"
            value={filters.to || ''}
            onChange={(v) => updateFilter('to', v)}
            type="datetime-local"
          />
        </div>
      </Card>

      {/* Results */}
      <Card>
        {isLoading ? (
          <TableSkeleton message="Loading attempts..." />
        ) : error ? (
          <ErrorState message="Failed to load attempts" />
        ) : (
          <>
            <Table<AttemptListItem>
              columns={columns}
              data={data?.attempts || []}
              keyField="id"
              onRowClick={(row) => setSelectedAttempt(row)}
              emptyMessage="No attempts found matching the filters"
            />
            {data && (
              <Pagination
                total={data.pagination.total}
                limit={data.pagination.limit}
                offset={data.pagination.offset}
                onPageChange={(offset) =>
                  setFilters((prev: AttemptQueryParams) => ({ ...prev, offset }))
                }
              />
            )}
          </>
        )}
      </Card>

      </>
      )}

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedAttempt}
        onClose={() => setSelectedAttempt(null)}
        title="Attempt Details"
      >
        {selectedAttempt && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500">Status</p>
                <StatusBadge
                  status={getDeliveryStatusType(selectedAttempt.status)}
                  label={selectedAttempt.status.toUpperCase()}
                />
              </div>
              <div>
                <p className="text-xs text-slate-500">Provider</p>
                <StatusBadge
                  status={selectedAttempt.provider === 'apns' ? 'info' : 'success'}
                  label={selectedAttempt.provider.toUpperCase()}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500">App ID</p>
                <p className="text-sm font-medium">{selectedAttempt.appId}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Event Type</p>
                <p className="text-sm font-medium">{selectedAttempt.eventType}</p>
              </div>
            </div>

            <div>
              <p className="text-xs text-slate-500">Notification ID</p>
              <p className="text-sm font-mono break-all">
                {selectedAttempt.notificationId}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500">Workspace ID</p>
                <p className="text-sm font-mono break-all">
                  {selectedAttempt.workspaceId}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">User ID</p>
                <p className="text-sm font-mono break-all">
                  {selectedAttempt.userId}
                </p>
              </div>
            </div>

            {selectedAttempt.errorCode && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-xs text-red-600 font-medium">Error</p>
                <p className="text-sm font-mono text-red-700">
                  {selectedAttempt.errorCode}
                </p>
                {selectedAttempt.errorMessage && (
                  <p className="text-sm text-red-600 mt-1">
                    {selectedAttempt.errorMessage}
                  </p>
                )}
              </div>
            )}

            {selectedAttempt.metadata && (
              <div>
                <p className="text-xs text-slate-500 mb-1">Metadata</p>
                <pre className="bg-slate-50 rounded-md p-3 text-xs overflow-x-auto">
                  {JSON.stringify(selectedAttempt.metadata, null, 2)}
                </pre>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-500">Created</p>
                <p>{formatDate(selectedAttempt.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Updated</p>
                <p>{formatDate(selectedAttempt.updatedAt)}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
